import type { ColumnDef } from "@tanstack/react-table";
import { capitalize, highlightText } from "@repo/utils";
import { createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";

import type { PrintRoute } from "~/providers/printing-provider";
import { SettingsHeader } from "~/components/layout/settings-header";
import { DataTable } from "~/components/miscellaneous/data-table";
import { DataTableColumnHeader } from "~/components/miscellaneous/data-table-column-header";
import { usePrinter } from "~/hooks/use-printer";
import { usePrintRouteManager } from "~/managers/use-print-route-manager";

export const Route = createFileRoute("/_main/settings/_layout/print-routes")({
  component: PrintRoutes,
});

function PrintRoutes() {
  const { t } = useTranslation("common");
  const { printRoutes, printers } = usePrinter();
  const { openPrintRouteDialog } = usePrintRouteManager();
  const columns: ColumnDef<PrintRoute>[] = [
    {
      id: t("name"),
      accessorKey: "name",
      filterFn: "includesString",
      size: 200,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("name")} />
      ),
      cell: ({ row: { original: printRoute }, table }) => {
        const globalFilter = table.getState().globalFilter as string;

        return (
          <p
            dangerouslySetInnerHTML={{
              __html: highlightText(printRoute.name, globalFilter) ?? "",
            }}
          ></p>
        );
      },
    },
    {
      id: t("PrintRoute.printers"),
      accessorKey: "name",
      filterFn: "includesString",
      size: 200,
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={t("PrintRoute.printers")}
        />
      ),
      cell: ({ row: { original: printRoute }, table }) => {
        const globalFilter = table.getState().globalFilter as string;

        return (
          <p
            dangerouslySetInnerHTML={{
              __html:
                highlightText(
                  printers
                    .filter((p) => p.printRoutes.includes(printRoute.id))
                    .map((p) => p.name)
                    .join(", "),
                  globalFilter,
                ) ?? "",
            }}
          ></p>
        );
      },
    },
  ];

  return (
    <>
      <SettingsHeader
        title={t("print_routes")}
        buttonLabel={capitalize(t("new_print_route"))}
        buttonAction={async () => {
          await openPrintRouteDialog();
        }}
      />

      <DataTable
        columns={columns}
        data={printRoutes}
        onSelect={openPrintRouteDialog}
      />
    </>
  );
}
